import { useState, useEffect, useCallback } from 'react';
import { searchCustomers, Customer } from 'api/activityReport/searchCustomers';
import { searchAddresses, Address } from 'api/activityReport/searchAddresses';

/**
 * Hook for searching customers
 * @param clientCustomerId - If provided, only fetch this customer (for client users)
 */
export const useCustomerSearch = (clientCustomerId?: number | string | null) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (searchKeyword: string = '') => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = { SearchKeyword: searchKeyword };
      // If clientCustomerId is provided, filter to only that customer
      if (clientCustomerId) {
        params.Id = Number(clientCustomerId);
      }
      const data = await searchCustomers(params);
      setCustomers(data);
    } catch (err: any) {
      console.error('Error searching customers:', err);
      setError(err.message || 'Failed to search customers');
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientCustomerId]);

  // Initial load
  useEffect(() => {
    search('');
  }, [search]);

  return {
    customers,
    isLoading,
    error,
    search,
  };
};

export const useAddressSearch = (customerId: string | number | null) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (searchKeyword: string = '') => {
    if (!customerId) {
      setAddresses([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await searchAddresses({ CustomerId: customerId, SearchKeyword: searchKeyword });
      setAddresses(data);
    } catch (err: any) {
      console.error('Error searching addresses:', err);
      setError(err.message || 'Failed to search addresses');
      setAddresses([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  // Load addresses when customer changes
  useEffect(() => {
    if (customerId) {
      search('');
    } else {
      setAddresses([]);
    }
  }, [customerId, search]);

  return {
    addresses,
    isLoading,
    error,
    search,
  };
};
